import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';

type MatchStatus = 'playing' | 'won' | 'lost';

const CANVAS_WIDTH = 480;
const CANVAS_HEIGHT = 300;
const PADDLE_WIDTH = 10;
const PADDLE_HEIGHT = 60;
const PADDLE_SPEED = 6;
const AI_SPEED = 4.5;
const BALL_SIZE = 8;
const BASE_BALL_SPEED = 4;
const WIN_SCORE = 5;
const TICK_MS = 16;

/**
 * Hidden "PONG.EXE" easter egg: classic two-paddle Pong against a simple
 * AI, styled to match the IBM CRT terminal theme. Only reachable while
 * terminal mode is active, triggered by typing "pong" (see App.handleGlobalKeydown).
 */
@Component({
  selector: 'app-pong-game',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onKeydown($event)',
    '(document:keyup)': 'onKeyup($event)'
  },
  template: `
    <div class="pong-panel">
      <div class="pong-header">
        <span class="pong-title">*** PONG.EXE ***</span>
        <span class="pong-score">{{ playerScore() }} : {{ aiScore() }}</span>
        <button class="pong-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <canvas #canvasEl [width]="canvasWidth" [height]="canvasHeight" class="pong-canvas"></canvas>
      @if (status() !== 'playing') {
        <div class="pong-gameover">{{ status() === 'won' ? 'YOU WIN!' : 'CPU WINS!' }} — PRESS ENTER TO PLAY AGAIN</div>
      } @else {
        <div class="pong-hint">ARROW UP/DOWN OR W/S TO MOVE</div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      height: 50vh;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.94);
      border-top: 2px solid #33ff66;
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
    }
    .pong-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
    }
    .pong-header {
      display: flex;
      gap: 1.5rem;
      align-items: center;
      color: #33ff66;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-size: 0.875rem;
    }
    .pong-score {
      color: #ffb000;
      font-size: 1rem;
      font-weight: 700;
    }
    .pong-close {
      background: none;
      border: 1px solid #33ff66;
      color: #33ff66;
      padding: 0.25rem 0.6rem;
      cursor: pointer;
      font-family: inherit;
      font-size: 0.75rem;
      border-radius: 0;
    }
    .pong-close:hover {
      background: #33ff66;
      color: #000000;
    }
    .pong-canvas {
      border: 1px solid #1a4d1a;
      image-rendering: pixelated;
      box-shadow: 0 0 24px rgba(51, 255, 102, 0.25);
    }
    .pong-gameover {
      color: #ffb000;
      font-size: 0.875rem;
      letter-spacing: 0.05em;
      text-align: center;
      animation: pong-blink 1s steps(1) infinite;
    }
    .pong-hint {
      color: rgba(51, 255, 102, 0.5);
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      text-align: center;
    }
    @keyframes pong-blink {
      50% { opacity: 0; }
    }
  `]
})
export class PongGame implements OnInit, OnDestroy {
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');

  close = output<void>();

  playerScore = signal(0);
  aiScore = signal(0);
  status = signal<MatchStatus>('playing');

  readonly canvasWidth = CANVAS_WIDTH;
  readonly canvasHeight = CANVAS_HEIGHT;

  private ctx?: CanvasRenderingContext2D;
  private intervalId?: ReturnType<typeof setInterval>;
  private pressedKeys = new Set<string>();

  private playerY = (CANVAS_HEIGHT - PADDLE_HEIGHT) / 2;
  private aiY = (CANVAS_HEIGHT - PADDLE_HEIGHT) / 2;
  private ballX = CANVAS_WIDTH / 2;
  private ballY = CANVAS_HEIGHT / 2;
  private ballVX = BASE_BALL_SPEED;
  private ballVY = BASE_BALL_SPEED * 0.4;

  ngOnInit(): void {
    this.ctx = this.canvasRef().nativeElement.getContext('2d') ?? undefined;
    this.resetBall(1);
    this.startLoop();
  }

  ngOnDestroy(): void {
    this.stopLoop();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }
    if (event.key === 'Enter' && this.status() !== 'playing') {
      this.restart();
      return;
    }
    if (['ArrowUp', 'ArrowDown', 'w', 'W', 's', 'S'].includes(event.key)) {
      this.pressedKeys.add(event.key.toLowerCase());
      event.preventDefault();
    }
  }

  onKeyup(event: KeyboardEvent): void {
    this.pressedKeys.delete(event.key.toLowerCase());
  }

  requestClose(): void {
    this.close.emit();
  }

  private startLoop(): void {
    this.stopLoop();
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
  }

  private stopLoop(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  private restart(): void {
    this.playerScore.set(0);
    this.aiScore.set(0);
    this.status.set('playing');
    this.playerY = (CANVAS_HEIGHT - PADDLE_HEIGHT) / 2;
    this.aiY = (CANVAS_HEIGHT - PADDLE_HEIGHT) / 2;
    this.resetBall(1);
  }

  private resetBall(direction: 1 | -1): void {
    this.ballX = CANVAS_WIDTH / 2;
    this.ballY = CANVAS_HEIGHT / 2;
    this.ballVX = BASE_BALL_SPEED * direction;
    this.ballVY = BASE_BALL_SPEED * (Math.random() > 0.5 ? 1 : -1) * 0.5;
  }

  private tick(): void {
    if (this.status() !== 'playing') {
      this.draw();
      return;
    }

    if (this.pressedKeys.has('arrowup') || this.pressedKeys.has('w')) {
      this.playerY = Math.max(0, this.playerY - PADDLE_SPEED);
    }
    if (this.pressedKeys.has('arrowdown') || this.pressedKeys.has('s')) {
      this.playerY = Math.min(this.canvasHeight - PADDLE_HEIGHT, this.playerY + PADDLE_SPEED);
    }

    const aiCenter = this.aiY + PADDLE_HEIGHT / 2;
    if (aiCenter < this.ballY - 12) {
      this.aiY = Math.min(this.canvasHeight - PADDLE_HEIGHT, this.aiY + AI_SPEED);
    } else if (aiCenter > this.ballY + 12) {
      this.aiY = Math.max(0, this.aiY - AI_SPEED);
    }

    this.ballX += this.ballVX;
    this.ballY += this.ballVY;

    if (this.ballY <= 0) {
      this.ballY = 0;
      this.ballVY *= -1;
    } else if (this.ballY >= this.canvasHeight - BALL_SIZE) {
      this.ballY = this.canvasHeight - BALL_SIZE;
      this.ballVY *= -1;
    }

    // Left paddle (player) collision.
    if (this.ballVX < 0 && this.ballX <= PADDLE_WIDTH && this.ballX + BALL_SIZE >= 0 &&
        this.ballY + BALL_SIZE >= this.playerY && this.ballY <= this.playerY + PADDLE_HEIGHT) {
      this.ballX = PADDLE_WIDTH;
      this.ballVX = Math.abs(this.ballVX) * 1.05;
      const hitOffset = (this.ballY - (this.playerY + PADDLE_HEIGHT / 2)) / (PADDLE_HEIGHT / 2);
      this.ballVY = hitOffset * BASE_BALL_SPEED;
    }

    // Right paddle (AI) collision.
    if (this.ballVX > 0 && this.ballX + BALL_SIZE >= this.canvasWidth - PADDLE_WIDTH &&
        this.ballY + BALL_SIZE >= this.aiY && this.ballY <= this.aiY + PADDLE_HEIGHT) {
      this.ballX = this.canvasWidth - PADDLE_WIDTH - BALL_SIZE;
      this.ballVX = -Math.abs(this.ballVX) * 1.05;
      const hitOffset = (this.ballY - (this.aiY + PADDLE_HEIGHT / 2)) / (PADDLE_HEIGHT / 2);
      this.ballVY = hitOffset * BASE_BALL_SPEED;
    }

    if (this.ballX < 0) {
      this.aiScore.update(s => s + 1);
      if (this.aiScore() >= WIN_SCORE) {
        this.status.set('lost');
      } else {
        this.resetBall(1);
      }
    } else if (this.ballX > this.canvasWidth) {
      this.playerScore.update(s => s + 1);
      if (this.playerScore() >= WIN_SCORE) {
        this.status.set('won');
      } else {
        this.resetBall(-1);
      }
    }

    this.draw();
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) {
      return;
    }

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

    ctx.strokeStyle = 'rgba(51, 255, 102, 0.3)';
    ctx.setLineDash([6, 8]);
    ctx.beginPath();
    ctx.moveTo(this.canvasWidth / 2, 0);
    ctx.lineTo(this.canvasWidth / 2, this.canvasHeight);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#33ff66';
    ctx.fillRect(0, this.playerY, PADDLE_WIDTH, PADDLE_HEIGHT);
    ctx.fillRect(this.canvasWidth - PADDLE_WIDTH, this.aiY, PADDLE_WIDTH, PADDLE_HEIGHT);

    ctx.fillStyle = '#ffb000';
    ctx.fillRect(this.ballX, this.ballY, BALL_SIZE, BALL_SIZE);
  }
}
